import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { api } from '~/api';
import { colors, control, fontFamily, radii, shadows, space, spacing, type } from '~/ui/tokens';
import { formatDateParts, formatIsoShort, formatRangeLabel, formatRangeLabelCompact } from '../lib/dateUtils';
import { formatGuestsLabel, formatGuestsLabelCompact, toEtgGuests, type GuestsSelection } from '../lib/guests';
import { getCountryForCityOrCode, searchPlaces, type Place } from '../lib/destinations';
import { encodeOccupancy } from '~/lib/occupancy';
import { detectUserCountry } from '~/lib/currency';
import { normalizeResidency, residencyName, setDefaultResidency } from '~/lib/residency';
import { Button } from './ui';
import { ReferenceDateRangePicker, type DateRange } from './ReferenceDateRangePicker';
import { GuestsPanel } from './GuestsPanel';
import { NationalityList } from './NationalityList';

export type { Place } from '../lib/destinations';
export type { DateRange } from './ReferenceDateRangePicker';

export interface SearchBarValue {
  destination: string;
  selectedDestination?: Place | null;
  dates: DateRange;
  guests: GuestsSelection;
  residency: string;
}

interface SearchBarProps {
  value: SearchBarValue;
  onChange: (value: SearchBarValue) => void;
  onSearch: () => void;
  style?: StyleProp<ViewStyle>;
  /** Quick filters rendered inside the card, below the fields — keeps them
   * legible over hero artwork and grouped with the search they modify. */
  footer?: React.ReactNode;
  compact?: boolean;
  buttonLabel?: string;
  elevated?: boolean;
  splitDates?: boolean;
  showPopularDestinations?: boolean;
  /** Render popular destinations as a permanent, full-width panel below the
   * fields (the expanded search) rather than as a dropdown under the field.
   * Suppresses the focus-triggered destination dropdown so there is a single
   * destinations surface, not a nested second one. */
  inlinePopular?: boolean;
  /** Drop the surrounding card border/background so the search sits flush on
   * whatever surface hosts it (the expanded dropdown) — no box-in-a-box. */
  flat?: boolean;
  /** One-tap select from the permanent popular panel — receives the chosen
   * destination so the caller can run the search immediately. */
  onSelectDestination?: (label: string) => void;
  /** One-tap select from either the autocomplete list or the popular panel — receives the chosen
   * Place (hotel or city) along with the updated search value. */
  onSelectPlace?: (place: Place, currentSearch: SearchBarValue) => void;
  /** Put the keyboard caret in the destination field as soon as the bar mounts
   * — used by the home expanded search so one click opens and focuses. */
  autoFocus?: boolean;
  /** Render the field labels in white — used when the bar sits on a coloured
   * (Punch Pink) container so the labels stay legible. */
  onColor?: boolean;
  /** High-hierarchy field values (large destination text, a big day-number
   * date breakdown, a large primary guest count) — used by the homepage's
   * standalone search module. Left off everywhere else, including the nav's
   * expanded search panel, so that surface is untouched. */
  richFields?: boolean;
  /** Phone only: tapping the destination opens a full-screen search (input +
   * suggestions) instead of an anchored dropdown. */
  fullScreenDestination?: boolean;
  /** Phone: the Vio-style sheet rows (full-width destination, two date boxes,
   * guests on its own row, Search). Used only by the nav search sheet. */
  mobileSheet?: boolean;
}

export const POPULAR_DESTINATIONS: Place[] = [
  { kind: 'city', label: 'Maldives', sublabel: 'Maldives' },
  { kind: 'city', label: 'Bali', sublabel: 'Indonesia' },
  { kind: 'city', label: 'Los Angeles', sublabel: 'United States' },
  { kind: 'city', label: 'Phuket', sublabel: 'Thailand' },
  { kind: 'city', label: 'Bangkok', sublabel: 'Thailand' },
  { kind: 'city', label: 'New York', sublabel: 'United States' },
  { kind: 'city', label: 'Dubai', sublabel: 'United Arab Emirates' },
  { kind: 'city', label: 'London', sublabel: 'United Kingdom' },
  { kind: 'city', label: 'Paris', sublabel: 'France' },
];

export function ReferenceSearchBar({ value, onChange, onSearch, footer, style, compact = false, buttonLabel = 'Get a rate', elevated = false, splitDates = false, showPopularDestinations = false, inlinePopular = false, flat = false, onSelectDestination, onSelectPlace, autoFocus = false, onColor = false, richFields = false, fullScreenDestination = false, mobileSheet = false }: SearchBarProps) {
  const [activeField, setActiveField] = useState<'dates' | 'checkin' | 'checkout' | 'guests' | 'nationality' | null>(null);
  const [nationalityError, setNationalityError] = useState(false);
  // Bumped when Search is pressed with no destination, so the field takes focus instead.
  const [destinationPrompt, setDestinationPrompt] = useState(0);
  const { width } = useWindowDimensions();
  // Non-rich usage is the nav bar's own search panel (NavSearchBar) — its
  // stacked/mobile threshold matches TopNav's compact breakpoint (1200) so the
  // fields inside don't stay in a "desktop" layout under an already-compact
  // bar. The homepage's richFields module is a standalone card with its own
  // tuned breakpoint and is unaffected.
  const stacked = width < (richFields ? 1024 : 1200);
  const denseDesktop = richFields && !stacked && width < 1200;
  const cardRef = useRef<View>(null);
  const mobileDateAnchorRef = useRef<View>(null);
  const checkinAnchorRef = useRef<View>(null);
  const checkoutAnchorRef = useRef<View>(null);
  const guestsAnchorRef = useRef<View>(null);
  const nationalityAnchorRef = useRef<View>(null);
  const popoverRef = useRef<View>(null);
  const searchButtonRef = useRef<View>(null);

  // After the date popover closes via Apply, move focus to the search CTA —
  // picking dates is normally the last field filled before searching, and on
  // mobile the button can sit off the initial view, easy to lose track of
  // once the calendar collapses.
  function focusSearchButton() {
    const node = searchButtonRef.current as unknown as HTMLElement | null;
    const trigger = node?.querySelector<HTMLElement>('[role="button"]') ?? node;
    requestAnimationFrame(() => trigger?.focus({ preventScroll: false }));
  }

  const activeAnchorRef = activeField === 'checkin'
    ? checkinAnchorRef
    : activeField === 'checkout'
      ? checkoutAnchorRef
      : activeField === 'guests'
        ? guestsAnchorRef
        : activeField === 'nationality'
          ? nationalityAnchorRef
          : mobileDateAnchorRef;

  // Which side of the range this popover edits, if any — passed through to
  // ReferenceDateRangePicker so tapping "Check-out" updates check-out (not
  // check-in) and vice versa. The combined "Dates" field (mobile) keeps the
  // original pick-start-then-end flow.
  const dateMode = activeField === 'checkin' ? 'checkin' : activeField === 'checkout' ? 'checkout' : undefined;
  const isDateField = activeField === 'dates' || activeField === 'checkin' || activeField === 'checkout';
  // On a stacked (mobile) layout the calendar takes over the full screen
  // (below) instead of the anchored dropdown desktop uses.
  const showFullScreenCalendar = stacked && isDateField;
  // Hero and the phone sheet give nationality its own box; the nav bar's desktop
  // guests dropdown still carries it.
  const ownNationalityBox = richFields || (mobileSheet && stacked);
  const suggestedNationality = normalizeResidency(detectUserCountry()) || 'in';

  // Keyboard: when a field's popover or full-screen picker closes, return focus
  // to that field instead of dropping it on the page body.
  const lastFieldRef = useRef<typeof activeField>(null);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (activeField) {
      lastFieldRef.current = activeField;
      return;
    }
    const last = lastFieldRef.current;
    lastFieldRef.current = null;
    if (!last) return;
    const anchor = (last === 'checkin' ? checkinAnchorRef : last === 'checkout' ? checkoutAnchorRef : last === 'guests' ? guestsAnchorRef : last === 'nationality' ? nationalityAnchorRef : mobileDateAnchorRef).current as unknown as HTMLElement | null;
    const timer = setTimeout(() => {
      const current = document.activeElement as HTMLElement | null;
      if (!current || current === document.body || current.getAttribute('role') !== 'button') {
        anchor?.querySelector<HTMLElement>('[role="button"]')?.focus?.();
      }
    }, 80);
    return () => clearTimeout(timer);
  }, [activeField]);

  // Collapse an open date/guests popover on any click outside the bar. A
  // fixed-position dismiss layer can't be trusted here: RN Web wraps the page
  // in a transformed ScrollView, which becomes the containing block for fixed
  // elements, so the layer lands off-screen. A document listener is exact.
  useEffect(() => {
    if (!activeField || typeof document === 'undefined') return;
    function handleOutside(event: MouseEvent) {
      // The full-screen mobile calendar has its own explicit close button and
      // isn't anchored under the bar at all — treating every tap inside it as
      // "outside" would close it the instant a day cell is pressed.
      if (showFullScreenCalendar) return;
      const node = cardRef.current as unknown as HTMLElement | null;
      const popoverNode = popoverRef.current as unknown as HTMLElement | null;
      if (event.target instanceof Node && !node?.contains(event.target) && !popoverNode?.contains(event.target)) {
        setActiveField(null);
      }
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setActiveField(null);
      const anchor = activeAnchorRef.current as unknown as HTMLElement | null;
      const trigger = anchor?.querySelector<HTMLElement>('[role="button"]') ?? anchor;
      requestAnimationFrame(() => trigger?.focus({ preventScroll: true }));
    }
    document.addEventListener('mousedown', handleOutside, true);
    document.addEventListener('keydown', handleEscape, true);
    return () => {
      document.removeEventListener('mousedown', handleOutside, true);
      document.removeEventListener('keydown', handleEscape, true);
    };
  }, [activeField, showFullScreenCalendar]);

  // Any path that actually fires the search (button, Enter key) must also
  // drop whatever field was open — the date/guests popover otherwise stays
  // mounted (with its portal, for the desktop dropdown) even once the page
  // navigates to results, since navigation doesn't itself unmount this bar.
  function handleSearch() {
    if (!value.destination.trim() && !value.selectedDestination) {
      setActiveField(null);
      setDestinationPrompt((tick) => tick + 1);
      return;
    }
    setActiveField(null);
    onSearch();
  }

  function handleSelectPlace(place: Place) {
    const nextSearch = { ...value, destination: place.label, selectedDestination: place };
    onChange(nextSearch);
    if (onSelectPlace) {
      onSelectPlace(place, nextSearch);
    } else if (onSelectDestination && place.kind === 'city') {
      onSelectDestination(place.label);
    }
  }

  function selectPopular(place: Place) {
    const nextSearch = { ...value, destination: place.label, selectedDestination: place };
    onChange(nextSearch);
    if (onSelectPlace) {
      onSelectPlace(place, nextSearch);
    } else if (onSelectDestination) {
      onSelectDestination(place.label);
    }
  }

  return (
    <View ref={cardRef} style={[styles.card, elevated && styles.cardElevated, compact && !stacked && styles.cardCompact, richFields && styles.cardRich, flat && styles.cardFlat, style]}>
      <View style={[styles.bar, compact && !stacked && styles.barCompact, richFields && styles.barRich, richFields && !stacked && styles.barRichDesktop, stacked && styles.barStacked, richFields && stacked && styles.barRichStacked, flat && styles.barFlat, mobileSheet && stacked && styles.barSheet]}>
        {mobileSheet && stacked ? (
          <>
            <DestinationField
              value={value.destination}
              selectedPlace={value.selectedDestination}
              onChange={(destination) => onChange({ ...value, destination, selectedDestination: null })}
              onSelect={handleSelectPlace}
              onInteract={() => setActiveField(null)}
              onSubmit={handleSearch}
              wrapperStyle={styles.fieldStacked}
              fullScreen
              bare
              searchDates={value.dates}
              guests={value.guests}
              focusSignal={destinationPrompt}
            />
            <View style={styles.sheetDates}>
              <View ref={checkinAnchorRef} style={styles.sheetDateHalf}>
                <SheetField icon="calendar-outline" label="Check-in" text={value.dates.start ? formatSheetDate(value.dates.start) : 'Check-in'} inset filled={Boolean(value.dates.start)} onPress={() => setActiveField('checkin')} />
              </View>
              <View style={styles.sheetDateDivider} />
              <View ref={checkoutAnchorRef} style={styles.sheetDateHalf}>
                <SheetField icon="calendar-outline" label="Check-out" text={value.dates.end ? formatSheetDate(value.dates.end) : 'Check-out'} inset filled={Boolean(value.dates.end)} onPress={() => setActiveField('checkout')} />
              </View>
            </View>
            <View ref={guestsAnchorRef} style={styles.fieldStacked}>
              <SheetField icon="people-outline" label="Guests and rooms" text={formatGuestsLabel(value.guests)} filled onPress={() => setActiveField(current => current === 'guests' ? null : 'guests')} />
            </View>
            <View ref={nationalityAnchorRef} style={styles.fieldStacked}>
              <SheetField icon="globe-outline" label="Lead guest" text={`Nationality: ${residencyName(normalizeResidency(value.residency) || suggestedNationality)}`} filled onPress={() => setActiveField(current => current === 'nationality' ? null : 'nationality')} />
            </View>
            <View ref={searchButtonRef}>
              <Pressable accessibilityRole="button" accessibilityLabel="Search hotels" onPress={handleSearch} style={({ pressed }) => [styles.sheetSearch, pressed && { opacity: 0.9 }]}>
                <Ionicons name="search" size={20} color={colors.card} />
                <Text style={styles.sheetSearchText}>Search</Text>
              </Pressable>
            </View>
          </>
        ) : richFields ? (
          <>
            <DestinationField
              value={value.destination}
              selectedPlace={value.selectedDestination}
              onChange={(destination) => onChange({ ...value, destination, selectedDestination: null })}
              onSelect={handleSelectPlace}
              onInteract={() => setActiveField(null)}
              onSubmit={handleSearch}
              wrapperStyle={styles.fieldStacked}
              showPopularDestinations={showPopularDestinations && !inlinePopular}
              autoFocus={autoFocus}
              richFields
              stacked={stacked}
              searchDates={value.dates}
              guests={value.guests}
              focusSignal={destinationPrompt}
            />

            {stacked ? (
              <View ref={mobileDateAnchorRef} style={[styles.anchorGroupRich, activeField === 'dates' && styles.anchorGroupActive]}>
                <MobileDateField
                  active={activeField === 'dates'}
                  start={value.dates.start}
                  end={value.dates.end}
                  onPress={() => setActiveField(current => current === 'dates' ? null : 'dates')}
                  onEscape={() => setActiveField(null)}
                />
              </View>
            ) : (
              <>
                <View ref={checkinAnchorRef} style={[styles.anchorGroupRich, activeField === 'checkin' && styles.anchorGroupActive]}>
                  <RichDateField label="Check-in" iso={value.dates.start} dense={denseDesktop} active={activeField === 'checkin'} onPress={() => setActiveField(current => current === 'checkin' ? null : 'checkin')} onEscape={() => setActiveField(null)} />
                </View>
                <View ref={checkoutAnchorRef} style={[styles.anchorGroupRich, activeField === 'checkout' && styles.anchorGroupActive]}>
                  <RichDateField label="Check-out" iso={value.dates.end} dense={denseDesktop} active={activeField === 'checkout'} onPress={() => setActiveField(current => current === 'checkout' ? null : 'checkout')} onEscape={() => setActiveField(null)} />
                </View>
              </>
            )}

            <View ref={guestsAnchorRef} style={[styles.anchorGroupRich, styles.guestAnchorGroup, activeField === 'guests' && styles.anchorGroupActive]}>
              <RichGuestsField guests={value.guests} stacked={stacked} active={activeField === 'guests'} onPress={() => setActiveField(current => current === 'guests' ? null : 'guests')} onEscape={() => setActiveField(null)} />
            </View>

            <View ref={nationalityAnchorRef} style={[styles.anchorGroupRich, styles.guestAnchorGroup, activeField === 'nationality' && styles.anchorGroupActive]}>
              <RichNationalityField residency={value.residency} suggested={suggestedNationality} stacked={stacked} active={activeField === 'nationality'} onPress={() => setActiveField(current => current === 'nationality' ? null : 'nationality')} onEscape={() => setActiveField(null)} />
            </View>

            {stacked ? (
              <View ref={searchButtonRef}>
                <Button label={buttonLabel} onPress={handleSearch} labelStyle={styles.searchButtonLabelRich} style={[styles.searchButton, styles.searchButtonRich, styles.searchButtonRichStacked]} />
              </View>
            ) : null}

          </>
        ) : (
          <>
        {/* Destination takes roughly half the bar on the wide layout — it is
            the field people reach for first. */}
        <DestinationField
          value={value.destination}
          selectedPlace={value.selectedDestination}
          onChange={(destination) => onChange({ ...value, destination, selectedDestination: null })}
          onSelect={handleSelectPlace}
          onInteract={() => setActiveField(null)}
          onSubmit={handleSearch}
          wrapperStyle={[stacked ? styles.fieldStacked : styles.fieldDestination, richFields && styles.fieldDestinationRich]}
          compact={compact && !stacked}
          showPopularDestinations={showPopularDestinations && !inlinePopular}
          autoFocus={autoFocus}
          onColor={onColor}
          richFields={false}
          fullScreen={fullScreenDestination && stacked}
          searchDates={value.dates}
          guests={value.guests}
              focusSignal={destinationPrompt}
        />

        {/* Narrow layout pairs dates and guests on one line rather than
            stacking every field, which otherwise pushes the button below
            the fold on a phone. */}
        {splitDates && !stacked ? <>
          <View ref={checkinAnchorRef} style={[styles.desktopDateField, activeField === 'checkin' && styles.anchorGroupActive]}>
            <FieldButton label="Check-in" displayValue={value.dates.start ? formatIsoShort(value.dates.start) : 'Add date'} icon="calendar-outline" onPress={() => setActiveField(current => current === 'checkin' ? null : 'checkin')} compact={compact} onColor={onColor} onEscape={() => setActiveField(null)} />
          </View>
          <View ref={checkoutAnchorRef} style={[styles.desktopDateField, activeField === 'checkout' && styles.anchorGroupActive]}>
            <FieldButton label="Check-out" displayValue={value.dates.end ? formatIsoShort(value.dates.end) : 'Add date'} icon="calendar-outline" onPress={() => setActiveField(current => current === 'checkout' ? null : 'checkout')} compact={compact} onColor={onColor} onEscape={() => setActiveField(null)} />
          </View>
          <View ref={guestsAnchorRef} style={[styles.desktopGuestField, activeField === 'guests' && styles.anchorGroupActive]}>
            <FieldButton label="Guests & Rooms" displayValue={formatGuestsLabel(value.guests)} icon="people-outline" onPress={() => setActiveField(current => current === 'guests' ? null : 'guests')} compact={compact} onColor={onColor} onEscape={() => setActiveField(null)} />
          </View>
        </> : <View style={[styles.fieldPair, stacked && styles.fieldPairStacked]}>
          <View ref={mobileDateAnchorRef} style={[styles.anchorGroup, activeField !== 'guests' && activeField != null && styles.anchorGroupActive]}>
              <FieldButton
                label="Dates"
                displayValue={
                  stacked
                    ? formatRangeLabelCompact(value.dates.start, value.dates.end)
                    : formatRangeLabel(value.dates.start, value.dates.end)
                }
                icon="calendar-outline"
                onPress={() => setActiveField(current => current === 'dates' ? null : 'dates')}
                compact={compact && !stacked}
                stacked={stacked}
                onColor={onColor}
                onEscape={() => setActiveField(null)}
              />
          </View>

          <View ref={guestsAnchorRef} style={[styles.anchorGroup, styles.guestAnchorGroup, activeField === 'guests' && styles.anchorGroupActive]}>
              <FieldButton
                label="Guests & Rooms"
                displayValue={stacked ? formatGuestsLabelCompact(value.guests) : formatGuestsLabel(value.guests)}
                icon="people-outline"
                onPress={() => setActiveField(current => current === 'guests' ? null : 'guests')}
                compact={compact && !stacked}
                stacked={stacked}
                onColor={onColor}
                onEscape={() => setActiveField(null)}
              />
          </View>
        </View>}

        <View ref={searchButtonRef}>
          <Button
            label={buttonLabel}
            onPress={handleSearch}
            size={compact ? 'sm' : 'default'}
            style={[styles.searchButton, compact && !stacked && styles.searchButtonCompact, stacked && styles.searchButtonStacked, onColor && styles.searchButtonOnColor]}
          />
        </View>

          </>
        )}
      </View>

      {activeField && !showFullScreenCalendar ? (
        <AnchoredPopover
          anchorRef={activeAnchorRef}
          popoverRef={popoverRef}
          align={!stacked && (activeField === 'guests' || activeField === 'nationality') ? 'right' : 'left'}
          // On mobile the anchor (half the "dates + guests" row) is much
          // narrower than the calendar/guests content needs — without an
          // explicit width here, AnchoredPopover falls back to that narrow
          // rect.width and squeezes the whole panel into a sliver. Requesting
          // the full tracked window width lets its own viewport-gutter clamp
          // size it correctly instead.
          preferredWidth={activeField === 'nationality' ? (stacked ? undefined : 340) : activeField === 'guests' ? (stacked ? undefined : 384) : (stacked ? width : 680)}
          revealOnOpen={isDateField}
          onEscape={() => setActiveField(null)}
        >
          {activeField === 'nationality' ? (
            <NationalityList
              value={normalizeResidency(value.residency)}
              onSelect={(code) => {
                setDefaultResidency(code);
                setNationalityError(false);
                onChange({ ...value, residency: code });
                setActiveField(null);
              }}
            />
          ) : activeField === 'guests' ? (
            <GuestsPanel
              visible
              value={value.guests}
              onChange={(guests) => onChange({ ...value, guests })}
              onClose={() => setActiveField(null)}
              nationality={value.residency}
              suggestedNationality={normalizeResidency(detectUserCountry()) || 'in'}
              onNationalityChange={(code) => {
                setDefaultResidency(code);
                setNationalityError(false);
                onChange({ ...value, residency: code });
              }}
              promptNationality={nationalityError}
              showNationality={!ownNationalityBox}
            />
          ) : (
            <ReferenceDateRangePicker value={value.dates} onChange={dates => onChange({ ...value, dates })} onApply={() => { setActiveField(null); focusSearchButton(); }} mode={dateMode} />
          )}
        </AnchoredPopover>
      ) : null}

      {/* Mobile: the calendar takes over the full screen rather than a small
          anchored dropdown, which has too little room for two month grids on
          a phone. Its own close button (and Apply) replace the outside-click
          dismissal the desktop dropdown relies on. */}
      {showFullScreenCalendar ? (
        <Modal
          visible
          animationType="slide"
          presentationStyle="overFullScreen"
          statusBarTranslucent
          onRequestClose={() => setActiveField(null)}
        >
          <View style={styles.mobileCalendarScreen}>
            <View style={styles.mobileCalendarTopBar}>
              <Pressable accessibilityRole="button" accessibilityLabel="Close calendar" onPress={() => setActiveField(null)} style={styles.mobileCalendarClose}>
                <Ionicons name="close" size={22} color={colors.ink} />
              </Pressable>
            </View>
            <View style={styles.mobileCalendarBody}>
              <ReferenceDateRangePicker
                value={value.dates}
                onChange={dates => onChange({ ...value, dates })}
                onApply={() => { setActiveField(null); focusSearchButton(); }}
                mode={dateMode}
                fullScreen
              />
            </View>
          </View>
        </Modal>
      ) : null}

      {footer ? <View style={[styles.footer, richFields && styles.footerRich, richFields && stacked && styles.footerRichStacked]}>{footer}</View> : null}

      {richFields && !stacked ? (
        // Desktop hero: the CTA straddles the card's bottom edge, half in, half out.
        <View pointerEvents="box-none" style={styles.heroCtaWrap}>
          <View ref={searchButtonRef}>
            <Button label={buttonLabel} onPress={handleSearch} labelStyle={styles.searchButtonLabelRich} style={[styles.searchButton, styles.heroCta]} />
          </View>
        </View>
      ) : null}

      {inlinePopular ? (
        <View style={styles.inlinePopular}>
          <Text style={styles.inlinePopularHeading}>Popular destinations</Text>
          <View style={styles.inlinePopularGrid}>
            {POPULAR_DESTINATIONS.map((place) => (
              <PopularItem key={place.label} place={place} stacked={stacked} onPress={() => selectPopular(place)} />
            ))}
          </View>
        </View>
      ) : null}

    </View>
  );
}

/** Destination autocomplete: local matches merged with the server's regions and
 * hotels, de-duplicated and ranked. Shared by the in-bar field and the desktop
 * nav search dropdown. */
export function useDestinationSuggestions(value: string): Place[] {
  const [apiPlaces, setApiPlaces] = useState<Place[]>([]);
  const latestRequest = useRef(0);
  const localSuggestions = useMemo(() => searchPlaces(value, 5), [value]);

  useEffect(() => {
    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setApiPlaces([]);
      return;
    }

    const reqId = ++latestRequest.current;
    const timer = setTimeout(() => {
      api.search
        .destinations({ query: trimmed })
        .then((response) => {
          if (reqId !== latestRequest.current) return;
          const hotels: Place[] = (response.data?.hotels ?? []).map((h) => ({
            kind: 'hotel',
            label: h.name,
            sublabel: 'Hotel',
            hid: h.hid,
            id: String(h.hid),
          }));
          const regions: Place[] = (response.data?.regions ?? []).map((r) => ({
            kind: 'city',
            label: r.name,
            sublabel: r.countryCode || r.type || 'Destination',
            id: String(r.regionId),
          }));
          setApiPlaces([...regions, ...hotels]);
        })
        .catch(() => {
          if (reqId !== latestRequest.current) return;
          setApiPlaces([]);
        });
    }, 250);

    return () => clearTimeout(timer);
  }, [value]);

  const suggestions = useMemo(() => {
    const trimmed = value.trim().toLowerCase();
    const allPlaces = [...apiPlaces, ...localSuggestions];
    if (!allPlaces.length) return [];

    const deduped: Place[] = [];
    const seen = new Map<string, Place>();

    for (const p of allPlaces) {
      const normLabel = p.label.trim().toLowerCase();
      const key = `${p.kind}-${normLabel}`;
      const existing = seen.get(key);

      if (!existing) {
        let sublabel = p.sublabel;
        if (p.kind === 'city') {
          const country = getCountryForCityOrCode(p.label, p.sublabel);
          if (country) sublabel = country;
        }
        const item: Place = { ...p, sublabel };
        seen.set(key, item);
        deduped.push(item);
      } else {
        if (!existing.id && p.id) {
          existing.id = p.id;
        }
        if (!existing.hid && p.hid) {
          existing.hid = p.hid;
        }
        if (p.kind === 'city') {
          const country = getCountryForCityOrCode(p.label, p.sublabel || existing.sublabel);
          if (country) existing.sublabel = country;
        }
      }
    }

    function getMatchScore(place: Place): number {
      const label = place.label.trim().toLowerCase();
      const sublabel = (place.sublabel || '').trim().toLowerCase();
      if (!trimmed) return 0;
      if (label === trimmed) return 0;
      if (label.startsWith(trimmed)) return 10;
      const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const wordRegex = new RegExp(`(?:^|\\s)${escaped}`);
      if (wordRegex.test(label)) return 20;
      if (sublabel.startsWith(trimmed)) return 30;
      if (label.includes(trimmed)) return 40;
      return 50;
    }

    deduped.sort((a, b) => {
      const kindA = a.kind === 'city' ? 0 : 100;
      const kindB = b.kind === 'city' ? 0 : 100;
      const matchA = getMatchScore(a);
      const matchB = getMatchScore(b);
      const totalA = kindA + matchA;
      const totalB = kindB + matchB;

      if (totalA !== totalB) {
        return totalA - totalB;
      }
      return a.label.localeCompare(b.label);
    });

    return deduped.slice(0, 7);
  }, [value, localSuggestions, apiPlaces]);
  return suggestions;
}

interface DestinationFieldProps {
  value: string;
  selectedPlace?: Place | null;
  onChange: (value: string) => void;
  onSelect: (place: Place) => void;
  onInteract?: () => void;
  onSubmit: () => void;
  wrapperStyle?: StyleProp<ViewStyle>;
  compact?: boolean;
  showPopularDestinations?: boolean;
  autoFocus?: boolean;
  onColor?: boolean;
  richFields?: boolean;
  stacked?: boolean;
  fullScreen?: boolean;
  bare?: boolean;
  searchDates?: DateRange;
  guests?: GuestsSelection;
  /** Changes when the field should grab focus (Search pressed with no destination). */
  focusSignal?: number;
}

function findVerticalScrollParent(node: HTMLElement): HTMLElement | null {
  for (let parent = node.parentElement; parent; parent = parent.parentElement) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight + 1) return parent;
  }
  return null;
}

function AnchoredPopover({ anchorRef, popoverRef, align, preferredWidth, revealOnOpen = false, onEscape, children }: {
  anchorRef: RefObject<View | null>;
  popoverRef: RefObject<View | null>;
  align: 'left' | 'right';
  preferredWidth?: number;
  revealOnOpen?: boolean;
  onEscape: () => void;
  children: ReactNode;
}) {
  const [position, setPosition] = useState<{ top: number; left: number; width: number; maxHeight: number; anchor: HTMLElement } | null>(null);
  const revealedAnchor = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => {
      const node = anchorRef.current as unknown as HTMLElement | null;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const viewportGutter = 16;
      const width = Math.min(preferredWidth ?? rect.width, window.innerWidth - viewportGutter * 2);
      const idealLeft = align === 'right' ? rect.right + window.scrollX - width : rect.left + window.scrollX;
      const left = Math.max(viewportGutter + window.scrollX, Math.min(idealLeft, window.scrollX + window.innerWidth - viewportGutter - width));
      const top = rect.bottom + window.scrollY + 8;
      // Dates can use the full viewport after the page scrolls to reveal them.
      // Other popovers retain the space-below cap, and short viewports still
      // scroll within the popover when the content cannot fit.
      const maxHeight = revealOnOpen
        ? Math.max(160, window.innerHeight - viewportGutter * 2)
        : Math.max(160, window.innerHeight - viewportGutter - (top - window.scrollY));
      setPosition({ top, left, width, maxHeight, anchor: node });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [align, anchorRef, preferredWidth, revealOnOpen]);

  useEffect(() => {
    if (!revealOnOpen || !position || position.anchor !== (anchorRef.current as unknown as HTMLElement | null) || revealedAnchor.current === position.anchor) return;
    const frame = requestAnimationFrame(() => {
      const popover = popoverRef.current as unknown as HTMLElement | null;
      if (!popover) return;
      revealedAnchor.current = position.anchor;
      const overflow = popover.getBoundingClientRect().bottom - (window.innerHeight - 16);
      if (overflow > 0) {
        const scrollParent = findVerticalScrollParent(position.anchor);
        (scrollParent ?? window).scrollBy({ top: overflow, behavior: 'smooth' });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [anchorRef, popoverRef, position, revealOnOpen]);

  useEffect(() => {
    if (!revealOnOpen || !position) return;
    const popover = popoverRef.current as unknown as HTMLElement | null;
    if (!popover) return;

    const handleWheel = (event: WheelEvent) => {
      if (!event.deltaY) return;
      // The calendar contains nested scroll views. Let one of them handle the
      // wheel only while it can actually scroll in that direction; otherwise
      // send the gesture to the page so opening the calendar does not trap it.
      let node = event.target instanceof Element ? event.target : null;
      while (node && popover.contains(node)) {
        const element = node as HTMLElement;
        const overflowY = window.getComputedStyle(element).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && element.scrollHeight > element.clientHeight + 1) {
          const canScroll = event.deltaY > 0
            ? element.scrollTop + element.clientHeight < element.scrollHeight - 1
            : element.scrollTop > 1;
          if (canScroll) return;
        }
        if (node === popover) break;
        node = node.parentElement;
      }

      event.preventDefault();
      const pixels = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaMode === 2 ? event.deltaY * window.innerHeight : event.deltaY;
      const anchor = anchorRef.current as unknown as HTMLElement | null;
      const scrollParent = anchor && findVerticalScrollParent(anchor);
      (scrollParent ?? window).scrollBy({ top: pixels });
    };

    popover.addEventListener('wheel', handleWheel, { capture: true, passive: false });
    return () => popover.removeEventListener('wheel', handleWheel, true);
  }, [anchorRef, popoverRef, revealOnOpen, Boolean(position)]);

  if (typeof document === 'undefined' || !position) return null;
  const { anchor: _anchor, ...popoverPosition } = position;
  return createPortal(
    <View
      ref={popoverRef}
      style={[styles.portalPopover, popoverPosition]}
      {...({ onKeyDownCapture: (event: KeyboardEvent) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        onEscape();
      } } as object)}
    >
      {children}
    </View>,
    document.body,
  );
}

function DestinationField({ value, selectedPlace, onChange, onSelect, onInteract, onSubmit, wrapperStyle, compact, showPopularDestinations, autoFocus, onColor, richFields, stacked, fullScreen, bare, searchDates, guests, focusSignal = 0 }: DestinationFieldProps) {
  const [focused, setFocused] = useState(false);
  const [screenOpen, setScreenOpen] = useState(false);
  const rowRef = useRef<View>(null);
  const [editedSinceFocus, setEditedSinceFocus] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const inputRef = useRef<TextInput>(null);
  const anchorRef = useRef<View>(null);
  const suggestionsRef = useRef<View>(null);
  const suggestions = useDestinationSuggestions(value);

  // Focus the caret as soon as the field mounts inside the expanded panel — the
  // small delay lets the modal finish fading in before focus lands (web).
  useEffect(() => {
    if (!autoFocus) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(timer);
  }, [autoFocus]);

  useEffect(() => {
    if (!focusSignal) return;
    if (fullScreen) setScreenOpen(true);
    else inputRef.current?.focus();
  }, [focusSignal]);

  // Keep the menu open for an exact match as well. This makes the field feel
  // like a proper combobox and leaves the user an explicit, keyboard-friendly
  // way to confirm the destination they typed.
  const showPopularPanel = focused && Boolean(showPopularDestinations) && !editedSinceFocus;
  const showSuggestions = focused && (showPopularPanel || suggestions.length > 0);

  function pick(place: Place) {
    onSelect(place);
    setFocused(false);
    setEditedSinceFocus(false);
    setHighlightedIndex(null);
    inputRef.current?.blur();
  }

  function handleKeyPress(event: { nativeEvent?: { key?: string }; key?: string; preventDefault?: () => void; stopPropagation?: () => void }) {
    const key = event.key ?? event.nativeEvent?.key;
    if (key === 'Escape') {
      event.preventDefault?.();
      event.stopPropagation?.();
      setFocused(false);
      setHighlightedIndex(null);
      inputRef.current?.blur();
      return;
    }
    if (!suggestions.length) return;
    if (key === 'ArrowDown') {
      event.preventDefault?.();
      setHighlightedIndex((index) => index == null ? 0 : Math.min(index + 1, suggestions.length - 1));
    } else if (key === 'ArrowUp') {
      event.preventDefault?.();
      setHighlightedIndex((index) => index == null ? suggestions.length - 1 : Math.max(index - 1, 0));
    } else if (key === 'Enter') {
      const selected = highlightedIndex == null ? null : suggestions[highlightedIndex];
      if (selected) {
        event.preventDefault?.();
        pick(selected);
      }
    }
  }

  if (fullScreen) {
    const showPopularRows = !editedSinceFocus && !value.trim();
    const rows = showPopularRows ? POPULAR_DESTINATIONS : suggestions;
    const closeScreen = () => {
      setScreenOpen(false);
      setEditedSinceFocus(false);
      setHighlightedIndex(null);
      // Hand keyboard focus back to the Destination row once the screen is gone.
      setTimeout(() => (rowRef.current as unknown as HTMLElement | null)?.focus?.(), 60);
    };
    // Keyboard: ArrowUp/Down move through the rows, Enter picks the highlighted
    // one (or the first), Escape closes the screen.
    const handleScreenKey = (event: { nativeEvent?: { key?: string }; key?: string; preventDefault?: () => void; stopPropagation?: () => void }) => {
      const key = event.key ?? event.nativeEvent?.key;
      if (key === 'Escape') {
        event.preventDefault?.();
        event.stopPropagation?.();
        closeScreen();
      } else if (key === 'ArrowDown' && rows.length) {
        event.preventDefault?.();
        setHighlightedIndex((index) => index == null ? 0 : Math.min(index + 1, rows.length - 1));
      } else if (key === 'ArrowUp' && rows.length) {
        event.preventDefault?.();
        setHighlightedIndex((index) => index == null ? rows.length - 1 : Math.max(index - 1, 0));
      } else if (key === 'Enter') {
        const chosen = highlightedIndex != null ? rows[highlightedIndex] : rows[0];
        if (chosen) {
          event.preventDefault?.();
          pick(chosen);
          closeScreen();
        }
      }
    };
    return (
      <View style={[wrapperStyle, styles.destWrap]}>
        {!compact && !bare ? <Text style={styles.fieldLabel}>Destination</Text> : null}
        <Pressable
          ref={rowRef}
          accessibilityRole="button"
          accessibilityLabel={value ? `Destination ${value}. Change` : 'Choose destination'}
          onPress={() => { onInteract?.(); setEditedSinceFocus(false); setScreenOpen(true); }}
          style={bare ? styles.sheetField : styles.fieldInput}
        >
          <Ionicons name={bare ? 'location-outline' : 'search'} size={bare ? 20 : 18} color={bare ? colors.inkSoft : colors.inkFaint} style={styles.fieldIcon} />
          <Text style={[styles.destText, bare && styles.sheetFieldText, !value && { color: colors.inkFaint }]} numberOfLines={1}>{value || (bare ? 'Where to?' : 'City or hotel name')}</Text>
        </Pressable>
        <Modal visible={screenOpen} animationType="slide" presentationStyle="overFullScreen" statusBarTranslucent onRequestClose={closeScreen}>
          <View style={styles.mobileDestScreen}>
            <View style={styles.mobileDestTop}>
              <View style={[styles.fieldInput, styles.fieldInputFocused, styles.mobileDestInput]}>
                <Ionicons name="search" size={18} color={colors.brand} style={styles.fieldIcon} />
                <TextInput
                  value={value}
                  autoFocus
                  onChangeText={(nextValue) => { setEditedSinceFocus(true); setHighlightedIndex(null); onChange(nextValue); }}
                  placeholder="City or hotel name"
                  placeholderTextColor={colors.inkFaint}
                  returnKeyType="search"
                  onKeyPress={handleScreenKey}
                  {...({ onKeyDown: handleScreenKey } as object)}
                  style={styles.destInput}
                />
                {value ? (
                  <Pressable accessibilityRole="button" accessibilityLabel="Clear destination" onPress={() => { setEditedSinceFocus(true); onChange(''); }} hitSlop={8}>
                    <Ionicons name="close-circle" size={18} color={colors.inkFaint} />
                  </Pressable>
                ) : null}
              </View>
              <Pressable accessibilityRole="button" accessibilityLabel="Close destination search" onPress={closeScreen} style={styles.mobileCalendarClose}>
                <Ionicons name="close" size={22} color={colors.ink} />
              </Pressable>
            </View>
            <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={styles.mobileDestList}>
              {showPopularRows ? <Text style={styles.suggestionsHeading}>Popular destinations</Text> : null}
              {rows.map((place, index) => (
                <SuggestionRow
                  key={`${place.kind}-${place.hid ?? place.id ?? place.label}`}
                  place={place}
                  active={index === highlightedIndex}
                  committed={selectedPlace?.kind === place.kind && selectedPlace.label === place.label}
                  onPress={() => { pick(place); closeScreen(); }}
                />
              ))}
            </ScrollView>
          </View>
        </Modal>
      </View>
    );
  }

  return (
    <View ref={anchorRef} style={[wrapperStyle, styles.destWrap]}>
      {!compact ? <Text style={[styles.fieldLabel, richFields && styles.fieldLabelRich, onColor && styles.fieldLabelOnColor]}>Destination</Text> : null}
      <View style={[styles.fieldInput, richFields && styles.fieldInputRich, richFields && stacked && styles.destinationInputRichStacked, compact && styles.fieldInputCompact, focused && styles.fieldInputFocused]}>
        <Ionicons name="search" size={richFields ? 20 : compact ? 15 : 18} color={colors.inkFaint} style={[styles.fieldIcon, richFields && styles.fieldIconRich]} />
        <TextInput
          ref={inputRef}
          value={value}
          onChangeText={(nextValue) => { setEditedSinceFocus(true); setHighlightedIndex(null); onChange(nextValue); }}
          placeholder="City or hotel name"
          placeholderTextColor={colors.inkFaint}
          onFocus={() => { onInteract?.(); setFocused(true); setEditedSinceFocus(false); setHighlightedIndex(null); }}
          // Delay the blur so a tap on a suggestion registers before the menu
          // unmounts — a plain onBlur hides the list before onPress can fire.
          onBlur={() => setTimeout(() => setFocused(false), 120)}
          onSubmitEditing={() => {
            if (focused && highlightedIndex != null && suggestions[highlightedIndex]) {
              pick(suggestions[highlightedIndex]);
            } else {
              onSubmit();
            }
          }}
          onKeyPress={handleKeyPress}
          {...({ onKeyDown: handleKeyPress } as object)}
          returnKeyType="search"
          style={[styles.destInput, richFields && styles.destInputRich, compact && styles.compactValue]}
        />
      </View>
      {showSuggestions ? (
        <AnchoredPopover
          anchorRef={anchorRef}
          popoverRef={suggestionsRef}
          align="left"
          preferredWidth={showPopularPanel ? 760 : undefined}
          onEscape={() => { setFocused(false); setHighlightedIndex(null); inputRef.current?.blur(); }}
        >
        <View
          style={[styles.suggestions, styles.suggestionsPortaled, showPopularPanel && styles.popularPanel]}
          {...({ onMouseDown: (e: any) => e.preventDefault?.() } as object)}
        >
          {!showPopularPanel ? suggestions.map((place, index) => (
            <SuggestionRow
              key={`${place.kind}-${place.hid ?? place.id ?? place.label}`}
              place={place}
              active={index === highlightedIndex}
              committed={selectedPlace?.kind === place.kind && selectedPlace.label === place.label}
              onPress={() => pick(place)}
            />
          )) : null}
          {showPopularPanel ? <View style={styles.popularSection}>
            <Text style={styles.suggestionsHeading}>Popular destinations</Text>
            <View style={styles.popularStrip}>
              {POPULAR_DESTINATIONS.map((place) => (
                <Pressable
                  key={place.label}
                  accessibilityRole="button"
                  accessibilityLabel={`Search ${place.label}`}
                  onPress={() => pick(place)}
                  style={styles.popularChip}
                >
                  <View style={styles.popularIcon}><Ionicons name="location-outline" size={17} color={colors.brand} /></View>
                  <View style={styles.popularCopy}><Text style={styles.popularChipText}>{place.label}</Text><Text style={styles.popularCountry}>{place.sublabel}</Text></View>
                </Pressable>
              ))}
            </View>
          </View> : null}
        </View>
        </AnchoredPopover>
      ) : null}
    </View>
  );
}

function formatSheetDate(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: '2-digit' });
}

function SheetField({ icon, label, text, filled, onPress, inset = false }: { icon: 'calendar-outline' | 'people-outline' | 'globe-outline'; label: string; text: string; filled: boolean; onPress: () => void; inset?: boolean }) {
  return (
    <Pressable accessibilityRole="button" accessibilityLabel={`${label}: ${text}`} onPress={onPress} style={[styles.sheetField, inset && styles.sheetFieldInset]}>
      <Ionicons name={icon} size={20} color={colors.inkSoft} />
      <Text style={[styles.sheetFieldText, !filled && { color: colors.inkFaint }]} numberOfLines={1}>{text}</Text>
    </Pressable>
  );
}

export function SuggestionRow({
  place,
  active,
  committed,
  onPress,
}: {
  place: Place;
  active: boolean;
  committed: boolean;
  onPress: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${place.label}, ${place.sublabel}`}
      accessibilityState={{ selected: committed }}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={StyleSheet.flatten([styles.suggestion, (hovered || active) && styles.suggestionActive])}
    >
      <Ionicons
        name={place.kind === 'hotel' ? 'business-outline' : 'location-outline'}
        size={16}
        color={colors.inkFaint}
      />
      <Text style={styles.suggestionCity} numberOfLines={1}>
        {place.label}
      </Text>
      <Text style={styles.suggestionCountry} numberOfLines={1}>
        {place.sublabel}
      </Text>
    </Pressable>
  );
}

function escapeProps(onEscape: () => void) {
  return { onKeyDown: (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    onEscape();
  } } as object;
}

function RichDateField({ label, iso, onPress, onEscape, dense = false, active = false }: { label: string; iso: string | null; onPress: () => void; onEscape: () => void; dense?: boolean; active?: boolean }) {
  const parts = iso ? formatDateParts(iso) : null;
  return (
    <Pressable accessibilityRole="button" accessibilityLabel={parts ? `${label}: ${parts.weekday} ${parts.day} ${parts.monthYear}` : `${label}: add date`} style={styles.richFieldPressable} onPress={onPress} {...escapeProps(onEscape)}>
      <Text style={[styles.fieldLabel, styles.fieldLabelRich]}>{label}</Text>
      <View style={[styles.fieldInput, styles.fieldInputRich, dense && styles.fieldInputRichDense, active && styles.fieldInputFocused]}>
        {parts ? (
          <View style={styles.richDateValue}>
            <Text style={[styles.richDatePrimary, dense && styles.richDatePrimaryDense]} numberOfLines={1}>{parts.day} {parts.monthLabel}</Text>
            <Text style={styles.richDateWeekday} numberOfLines={1}>{parts.weekday}</Text>
          </View>
        ) : (
          <Text style={styles.fieldValue}>Add date</Text>
        )}
      </View>
    </Pressable>
  );
}

function MobileDateField({ start, end, onPress, onEscape, active = false }: { start: string | null; end: string | null; onPress: () => void; onEscape: () => void; active?: boolean }) {
  return (
    <Pressable accessibilityRole="button" accessibilityLabel="Dates" style={styles.richFieldPressable} onPress={onPress} {...escapeProps(onEscape)}>
      <Text style={[styles.fieldLabel, styles.fieldLabelRich]}>Dates</Text>
      <View style={[styles.fieldInput, styles.mobileDateInput, active && styles.fieldInputFocused]}>
        <Ionicons name="calendar-outline" size={20} color={colors.inkFaint} style={styles.fieldIconRich} />
        <Text style={styles.mobileDateValue} numberOfLines={1}>{formatRangeLabelCompact(start, end)}</Text>
      </View>
    </Pressable>
  );
}

function RichGuestsField({ guests, onPress, onEscape, stacked, active = false }: { guests: GuestsSelection; onPress: () => void; onEscape: () => void; stacked?: boolean; active?: boolean }) {
  const adultsLabel = `${guests.adults} Adult${guests.adults === 1 ? '' : 's'}`;
  const roomsLabel = `${guests.rooms} Room${guests.rooms === 1 ? '' : 's'}`;
  const childrenLabel = guests.children > 0 ? `, ${guests.children} Child${guests.children === 1 ? '' : 'ren'}` : '';
  return (
    <Pressable accessibilityRole="button" accessibilityLabel="Guests & Rooms" style={styles.richFieldPressable} onPress={onPress} {...escapeProps(onEscape)}>
      <Text style={[styles.fieldLabel, styles.fieldLabelRich]}>Guests &amp; Rooms</Text>
      <View style={[styles.fieldInput, styles.fieldInputRich, stacked && styles.guestsInputRichStacked, active && styles.fieldInputFocused]}>
        {stacked ? <Ionicons name="person-outline" size={20} color={colors.inkFaint} style={styles.fieldIconRich} /> : null}
        <View style={styles.richGuestsValue}>
          <Text style={styles.richGuestsPrimary} numberOfLines={1}>{adultsLabel}</Text>
          <Text style={styles.richGuestsSecondary} numberOfLines={1}>{roomsLabel}{childrenLabel}</Text>
        </View>
        <Ionicons name="chevron-down" size={18} color={colors.inkFaint} />
      </View>
    </Pressable>
  );
}

function RichNationalityField({ residency, suggested, stacked, active, onPress, onEscape }: { residency: string; suggested: string; stacked?: boolean; active?: boolean; onPress: () => void; onEscape: () => void }) {
  const code = normalizeResidency(residency) || suggested;
  return (
    <Pressable accessibilityRole="button" accessibilityLabel={`Nationality: ${residencyName(code)}`} style={styles.richFieldPressable} onPress={onPress} {...escapeProps(onEscape)}>
      <Text style={[styles.fieldLabel, styles.fieldLabelRich]}>Nationality</Text>
      <View style={[styles.fieldInput, styles.fieldInputRich, stacked && styles.guestsInputRichStacked, active && styles.fieldInputFocused]}>
        <Ionicons name="globe-outline" size={20} color={colors.inkFaint} style={styles.fieldIconRich} />
        <View style={styles.richGuestsValue}>
          <Text style={styles.richGuestsPrimary} numberOfLines={1}>{residencyName(code)}</Text>
          <Text style={styles.richGuestsSecondary} numberOfLines={1}>Citizenship</Text>
        </View>
        <Ionicons name="chevron-down" size={18} color={colors.inkFaint} />
      </View>
    </Pressable>
  );
}

interface FieldButtonProps {
  label: string;
  displayValue: string;
  icon: keyof typeof Ionicons.glyphMap;
  onPress: () => void;
  compact?: boolean;
  stacked?: boolean;
  onColor?: boolean;
  onEscape: () => void;
}

function FieldButton({ label, displayValue, icon, onPress, compact, stacked, onColor, onEscape }: FieldButtonProps) {
  return (
    <Pressable accessibilityRole="button" accessibilityLabel={label} style={styles.fieldFlexible} onPress={onPress} {...escapeProps(onEscape)}>
      {!compact ? <Text style={[styles.fieldLabel, onColor && styles.fieldLabelOnColor]}>{label}</Text> : null}
      <View style={[styles.fieldInput, compact && styles.fieldInputCompact]}>
        <Ionicons name={icon} size={compact ? 15 : 18} color={colors.inkFaint} style={styles.fieldIcon} />
        <Text style={[styles.fieldValue, compact && styles.compactValue]} numberOfLines={1}>
          {displayValue}
        </Text>
      </View>
    </Pressable>
  );
}

// A borderless popular-destination row: pin + name + region, laid out three to
// a line. No tile, no border — just part of the one dropdown surface, with a
// quiet hover wash for affordance.
function PopularItem({ place, stacked, onPress }: { place: Place; stacked: boolean; onPress: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`Search ${place.label}, ${place.sublabel}`}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[styles.popularItem, stacked && styles.popularItemStacked, hovered && styles.popularItemHover]}
    >
      <Ionicons name="location-outline" size={18} color={colors.brand} />
      <View style={styles.popularItemCopy}>
        <Text style={styles.popularItemLabel} numberOfLines={1}>{place.label}</Text>
        <Text style={styles.popularItemSub} numberOfLines={1}>{place.sublabel}</Text>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.card,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.lineSoft,
    position: 'relative',
    zIndex: 100,
    overflow: 'visible',
  },
  cardFlat: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderRadius: 0,
  },
  cardElevated: {
    ...shadows.card,
  },
  cardCompact: {
    flex: 1,
    borderRadius: radii.full,
    borderWidth: 0,
    boxShadow: 'none',
  },
  // The homepage's standalone module: a larger radius and roomier bar so the
  // whole thing reads as a big, premium container rather than a form.
  cardRich: {
    borderRadius: 28,
  },
  bar: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: space.compact,
    padding: space.internal,
    // Sits above the footer so the destination autosuggest menu, which
    // overflows the bar downward, paints over (and stays clickable above)
    // the quick-filters row rather than behind it.
    position: 'relative',
    zIndex: 110,
  },
  barRich: {
    paddingTop: 28,
    paddingHorizontal: 32,
    paddingBottom: 0,
    gap: 12,
  },
  barRichDesktop: {
    alignItems: 'flex-end',
    ...({ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.3fr) minmax(0, 1.3fr)' } as object),
  },
  barRichStacked: {
    paddingTop: 22,
    paddingHorizontal: 20,
    paddingBottom: 0,
    gap: 12,
  },
  barCompact: {
    alignItems: 'center',
    gap: 0,
    padding: 2,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.full,
  },
  heroCtaWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: -28,
    alignItems: 'center',
    zIndex: 120,
  },
  heroCta: {
    minWidth: 240,
    height: 56,
    borderRadius: radii.full,
    ...shadows.card,
  },
  barSheet: {
    gap: space.compact,
  },
  barStacked: {
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
  },
  // Flat: fields sit flush on the host surface (no card padding around them).
  barFlat: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: space.compact,
  },
  // No rule between the fields and the quick filters: on a coloured search
  // module the hairline read as a hard white seam splitting one control.
  footer: {
    paddingHorizontal: space.card,
    paddingVertical: space.internal,
    position: 'relative',
    zIndex: 1,
  },
  footerRich: {
    paddingTop: 20,
    paddingHorizontal: 32,
    paddingBottom: 56,
  },
  footerRichStacked: {
    paddingTop: 20,
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  // Destination ~ half the wide bar. flexBasis:0 is only safe on a child of
  // the horizontal bar; the stacked layout drops this style entirely (the
  // field is full-width there).
  fieldDestination: {
    flexGrow: 1.8,
    flexBasis: 0,
    minWidth: 0,
  },
  fieldDestinationRich: {
    flexGrow: 1.7,
  },
  fieldStacked: {
    width: '100%',
  },
  // FieldButton's Pressable is always the sole child of a column-direction
  // anchor (desktopDateField/desktopGuestField, or anchorGroup) — never a
  // direct row child itself. A zero flex-basis there has no content-based
  // minimum to fall back on and collapses the field to zero height (visible
  // as Check-in/Check-out/Guests sinking to the row's bottom edge instead of
  // aligning with Destination); flexBasis:'auto' sizes to content instead,
  // while width still comes from the row anchor around it.
  fieldFlexible: {
    flexGrow: 1,
    flexBasis: 'auto',
    minWidth: 0,
  },
  // RichGuestsField sits alone (no row sibling to share width with) directly
  // inside the guests anchor column, so it needs the same fix as
  // fieldFlexible above — width:100% fills the anchor without the
  // collapse-to-zero-height bug a zero flex-basis would cause there.
  richFieldPressable: {
    width: '100%',
  },
  anchorGroupRich: {
    width: '100%',
    minWidth: 0,
    position: 'relative',
    zIndex: 20,
  },
  // Holds dates + guests. On the wide bar it behaves as two field-widths
  // of the row; stacked, it becomes a full-width two-up line.
  fieldPair: {
    flexDirection: 'row',
    gap: space.related,
    flexGrow: 1.55,
    flexBasis: 0,
    minWidth: 0,
    alignItems: 'center',
  },
  desktopDateField: { flexGrow: 1, flexBasis: 0, minWidth: 0, position: 'relative', zIndex: 20 },
  desktopGuestField: { flexGrow: 1.3, flexBasis: 0, minWidth: 0, position: 'relative', zIndex: 30 },
  fieldPairStacked: {
    flexGrow: 0,
    flexBasis: 'auto',
    alignSelf: 'stretch',
    width: '100%',
    alignItems: 'stretch',
  },
  anchorGroup: {
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 0,
    position: 'relative',
    zIndex: 20,
    justifyContent: 'center',
  },
  dateAnchorGroup: {
    // Extra share (over guests) so the weekday line — "Wednesday" is the
    // long pole — has room to sit on one line instead of eliding.
    flexGrow: 1.6,
  },
  guestAnchorGroup: {
    zIndex: 30,
  },
  anchorGroupActive: {
    zIndex: 200,
  },
  splitDateFields: {
    flexDirection: 'row',
    gap: space.related,
  },
  splitDateFieldAnchor: {
    flex: 1,
    minWidth: 0,
  },
  splitDateFieldsRich: {
    gap: space.compact,
  },
  sheetField: {
    height: 52,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.compact,
    paddingHorizontal: space.internal,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.md,
    backgroundColor: colors.card,
  },
  sheetFieldInset: {
    borderWidth: 0,
    borderRadius: 0,
  },
  sheetFieldText: {
    flex: 1,
    minWidth: 0,
    fontFamily: fontFamily.medium,
    fontSize: type.bodyRegular.fontSize,
    color: colors.ink,
  },
  sheetDates: {
    flexDirection: 'row',
    alignItems: 'stretch',
    width: '100%',
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.md,
    overflow: 'hidden',
  },
  sheetDateHalf: {
    flex: 1,
    minWidth: 0,
  },
  sheetDateDivider: {
    width: 1,
    backgroundColor: colors.line,
  },
  sheetSearch: {
    height: 52,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: space.compact,
    borderRadius: radii.full,
    backgroundColor: colors.brand,
  },
  sheetSearchText: {
    fontFamily: fontFamily.semibold,
    fontSize: type.bodyRegular.fontSize,
    color: colors.card,
  },
  mobileDestScreen: {
    flex: 1,
    backgroundColor: colors.card,
  },
  mobileDestTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.compact,
    paddingHorizontal: space.internal,
    paddingTop: space.component,
    paddingBottom: space.compact,
    borderBottomWidth: 1,
    borderBottomColor: colors.lineSoft,
  },
  mobileDestInput: {
    flex: 1,
  },
  mobileDestList: {
    paddingHorizontal: space.internal,
    paddingVertical: space.compact,
  },
  mobileCalendarScreen: {
    flex: 1,
    backgroundColor: colors.cream,
  },
  mobileCalendarTopBar: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: space.internal,
  },
  mobileCalendarClose: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.full,
    backgroundColor: colors.lineSoft,
  },
  mobileCalendarBody: {
    flex: 1,
    paddingHorizontal: space.internal,
    paddingBottom: space.internal,
  },
  portalPopover: {
    position: 'absolute',
    backgroundColor: colors.card,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.line,
    // The popover's `maxHeight` (set inline, clamped to the viewport — see
    // AnchoredPopover) can be shorter than its content on a small/mobile
    // screen; scrolling the popover itself — instead of clipping it — is
    // what keeps a tall calendar's Apply button reachable.
    ...({ overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch' } as object),
    // This portals straight to document.body, as a SIBLING of any open RN
    // Modal rather than a descendant of it — when the search bar itself is
    // opened inside a Modal (NavSearchBar), that Modal's own fixed layer
    // (react-native-web renders it around z-index 9999) would otherwise
    // composite entirely above this popover, hiding it behind the search
    // panel's own opaque content. 20000 clears that plus this app's other
    // known top-layer (ModalSheet's 10000/10001) with room to spare.
    zIndex: 20000,
    ...shadows.lift,
  },
  suggestionsPortaled: {
    position: 'relative',
    top: 0,
    left: 0,
    width: '100%',
    marginTop: 0,
    borderWidth: 0,
    boxShadow: 'none',
  },
  destWrap: {
    // Establishes a stacking context so the absolute suggestion menu paints
    // above the sibling fields to its right.
    position: 'relative',
    zIndex: 1000,
  },
  fieldLabel: {
    fontFamily: fontFamily.medium,
    ...control.inputLabel,
    color: colors.inkSoft,
    marginBottom: space.related,
  },
  fieldLabelRich: {
    marginBottom: 8,
    fontSize: 14,
    lineHeight: 18,
  },
  fieldLabelOnColor: {
    color: colors.white,
  },
  fieldInput: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.md,
    paddingHorizontal: space.compact,
    height: 48,
    backgroundColor: colors.card,
  },
  fieldInputFocused: {
    borderColor: colors.brand,
  },
  // Taller, more padded box for the richFields hierarchy (day-number date
  // breakdown, 2-line guests value, larger destination text) — applied
  // uniformly across every field so the row stays visually level and calm
  // rather than a row of cramped little boxes.
  fieldInputRich: {
    height: 80,
    paddingHorizontal: 18,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  fieldInputRichDense: {
    paddingHorizontal: 12,
  },
  destinationInputRichStacked: {
    height: 76,
    paddingHorizontal: 16,
    borderRadius: 14,
  },
  destInputRich: {
    fontFamily: fontFamily.regular,
    fontSize: 18,
  },
  richDateValue: {
    flex: 1,
    minWidth: 0,
  },
  richDatePrimary: {
    fontFamily: fontFamily.semibold,
    fontSize: 18,
    lineHeight: 22,
    color: colors.ink,
  },
  richDatePrimaryDense: {
    fontSize: 14,
    lineHeight: 19,
    letterSpacing: -0.5,
  },
  richDateWeekday: {
    marginTop: space.micro,
    fontFamily: fontFamily.regular,
    fontSize: 13,
    color: colors.inkFaint,
  },
  richGuestsValue: {
    flex: 1,
    minWidth: 0,
  },
  richGuestsPrimary: {
    fontFamily: fontFamily.semibold,
    fontSize: 18,
    lineHeight: 22,
    color: colors.ink,
  },
  richGuestsSecondary: {
    marginTop: space.micro,
    fontFamily: fontFamily.regular,
    fontSize: 13,
    color: colors.inkFaint,
  },
  fieldInputCompact: {
    height: 38,
    borderWidth: 0,
    borderRadius: 0,
    paddingHorizontal: space.related,
  },
  fieldIcon: {
    marginRight: space.related,
  },
  fieldIconRich: {
    marginRight: 12,
    flexShrink: 0,
  },
  mobileDateInput: {
    height: 68,
    borderRadius: 14,
    paddingHorizontal: 16,
  },
  mobileDateValue: {
    fontFamily: fontFamily.medium,
    fontSize: 18,
    lineHeight: 24,
    color: colors.ink,
  },
  guestsInputRichStacked: {
    height: 76,
    borderRadius: 14,
    paddingHorizontal: 16,
  },
  fieldValue: {
    fontFamily: fontFamily.regular,
    ...control.inputValue,
    color: colors.ink,
    flexShrink: 1,
  },
  destText: {
    flex: 1,
    fontFamily: fontFamily.regular,
    ...control.inputValue,
    color: colors.ink,
  },
  destInput: {
    flex: 1,
    fontFamily: fontFamily.regular,
    ...control.inputValue,
    color: colors.ink,
    height: '100%',
    ...({ outlineStyle: 'none' } as object),
  },
  compactValue: {
    fontSize: type.bodyRegular.fontSize,
  },
  suggestions: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 0,
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.md,
    paddingVertical: 0,
    overflow: 'hidden',
    zIndex: 1100,
    ...shadows.card,
  },
  popularPanel: {
    width: 760,
    right: 'auto',
  },
  suggestionsHeading: {
    paddingTop: space.micro,
    paddingBottom: space.micro,
    fontFamily: fontFamily.semibold,
    fontSize: type.metadata.fontSize,
    color: colors.inkFaint,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  popularSection: {
    paddingHorizontal: space.internal,
    paddingVertical: space.compact,
  },
  popularStrip: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: space.internal,
    rowGap: space.related,
  },
  popularChip: {
    width: '31%',
    minHeight: 52,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.related,
    paddingHorizontal: space.micro,
  },
  popularIcon: { width: 38, height: 38, borderRadius: radii.sm, backgroundColor: colors.skyTint, alignItems: 'center', justifyContent: 'center' },
  popularCopy: { flex: 1, minWidth: 0 },
  popularChipText: {
    fontFamily: fontFamily.medium,
    fontSize: type.bodyRegular.fontSize,
    color: colors.ink,
  },
  popularCountry: { marginTop: space.micro, fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
  suggestion: {
    width: '100%',
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.related,
    paddingVertical: space.compact,
    paddingHorizontal: space.internal,
  },
  suggestionActive: {
    backgroundColor: colors.skyTint,
  },
  suggestionCity: {
    flexShrink: 1,
    fontFamily: fontFamily.medium,
    fontSize: type.bodyLarge.fontSize,
    color: colors.ink,
  },
  suggestionCountry: {
    marginLeft: 'auto',
    paddingLeft: space.related,
    flexShrink: 0,
    fontFamily: fontFamily.regular,
    fontSize: type.bodyRegular.fontSize,
    color: colors.inkFaint,
  },
  searchButtonStacked: {
    width: '100%',
  },
  // Accent CTA for the coloured (brand blue) search module.
  searchButtonOnColor: {
    backgroundColor: colors.pink,
  },
  searchButton: {
    // Slightly trimmed from the full-height, full-padding default so the CTA
    // stays clearly primary without dominating the panel.
    minHeight: 46,
    paddingHorizontal: space.component,
  },
  // Matches the richFields field height so the button reads as a full row
  // member, not a thin strip tacked onto a tall search module.
  searchButtonRich: {
    width: 180,
    height: 80,
    minHeight: 80,
    paddingHorizontal: 18,
    paddingVertical: 0,
    borderRadius: 16,
  },
  searchButtonRichStacked: {
    width: '100%',
    height: 60,
    minHeight: 60,
    borderRadius: 14,
  },
  searchButtonLabelRich: {
    fontSize: 17,
    lineHeight: 22,
  },
  searchButtonCompact: {
    width: 86,
    minHeight: 36,
    borderRadius: 18,
    paddingHorizontal: space.compact,
    paddingVertical: 0,
  },
  // One clean surface: a hairline divides the fields from the suggestions; the
  // rows themselves carry no borders or tiles.
  inlinePopular: {
    borderTopWidth: 1,
    borderTopColor: colors.lineSoft,
    marginTop: space.internal,
    paddingTop: space.internal,
    gap: space.related,
  },
  inlinePopularHeading: {
    fontFamily: fontFamily.medium,
    fontSize: type.metadata.fontSize,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: colors.inkFaint,
    marginBottom: space.related,
  },
  inlinePopularGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -spacing(1),
    columnGap: space.internal,
    rowGap: space.micro,
  },
  popularItem: {
    flexGrow: 1,
    flexBasis: '30%',
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.compact,
    paddingVertical: space.related,
    paddingHorizontal: space.related,
    borderRadius: radii.md,
  },
  popularItemStacked: {
    flexBasis: 140,
    minWidth: 130,
  },
  popularItemHover: {
    backgroundColor: colors.lineSoft,
  },
  popularItemCopy: {
    flex: 1,
    minWidth: 0,
  },
  popularItemLabel: {
    fontFamily: fontFamily.medium,
    fontSize: type.bodyLarge.fontSize,
    color: colors.ink,
  },
  popularItemSub: {
    marginTop: space.micro,
    fontFamily: fontFamily.regular,
    fontSize: type.metadata.fontSize,
    color: colors.inkFaint,
  },
});
